/**
 * 站內即時提醒（2026-08-27）
 * 登入後每 POLL_MS 輪詢一次通知；發現新的未讀通知就彈出 toast，並維護頂欄未讀徽章。
 * 水位（已看過的最大 id）存 localStorage，重新整理不會把舊通知再彈一次。
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { trpc } from "../lib/trpc";
import { useAuth } from "./AuthContext";
import { selectToasts, type FeedItem } from "../../../shared/notification-feed";

/** 輪詢間隔；分頁在背景時瀏覽器不會觸發（react-query 預設不在背景輪詢） */
const POLL_MS = 30_000;
const SEEN_KEY = (userId: number) => `vehicle-console-notif-seen-${userId}`;

export interface ToastItem {
  id: number;
  title: string;
  body: string;
  path: string | null;
}

interface NotificationValue {
  unreadCount: number;
  toasts: ToastItem[];
  dismiss: (id: number) => void;
  markRead: (id: number) => void;
  refresh: () => void;
}

const NotificationContext = createContext<NotificationValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const enabled = !!user;

  const listQ = trpc.notification.myList.useQuery(undefined, {
    enabled,
    refetchInterval: enabled ? POLL_MS : false,
    staleTime: 0,
  });
  const unreadQ = trpc.notification.unreadCount.useQuery(undefined, {
    enabled,
    refetchInterval: enabled ? POLL_MS : false,
    staleTime: 0,
  });
  const markReadMut = trpc.notification.markRead.useMutation({
    onSuccess: () => {
      void listQ.refetch();
      void unreadQ.refetch();
    },
  });

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // 水位放 ref：只在輪詢回來時比對，不觸發重繪
  const seenRef = useRef<number | null>(null);
  const userIdRef = useRef<number | null>(null);

  // 換使用者（登入／登出／切帳號）時重置水位，改讀該使用者的 localStorage
  useEffect(() => {
    const uid = user?.id ?? null;
    if (userIdRef.current === uid) return;
    userIdRef.current = uid;
    setToasts([]);
    if (uid === null) {
      seenRef.current = null;
      return;
    }
    const saved = window.localStorage.getItem(SEEN_KEY(uid));
    const parsed = saved === null ? NaN : Number(saved);
    seenRef.current = Number.isFinite(parsed) ? parsed : null;
  }, [user?.id]);

  const data = listQ.data;
  useEffect(() => {
    const uid = userIdRef.current;
    if (!data || uid === null) return;
    const { toasts: fresh, nextSeenId } = selectToasts(data as unknown as FeedItem[], seenRef.current);
    if (nextSeenId !== seenRef.current) {
      seenRef.current = nextSeenId;
      window.localStorage.setItem(SEEN_KEY(uid), String(nextSeenId));
    }
    if (fresh.length === 0) return;
    setToasts((prev) => {
      const existing = new Set(prev.map((t) => t.id));
      const add = fresh
        .filter((n) => !existing.has(n.id))
        .map((n) => ({
          id: n.id,
          title: n.title,
          body: n.body,
          path: toInternalPathSafe(n.payload),
        }));
      return add.length ? [...prev, ...add] : prev;
    });
  }, [data]);

  const dismiss = useCallback((id: number) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const markRead = useCallback(
    (id: number) => {
      markReadMut.mutate({ id });
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [markReadMut],
  );
  const refresh = useCallback(() => {
    void listQ.refetch();
    void unreadQ.refetch();
  }, [listQ, unreadQ]);

  return (
    <NotificationContext.Provider
      value={{ unreadCount: unreadQ.data ?? 0, toasts, dismiss, markRead, refresh }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

/** 與 shared/notification-feed 的 toInternalPath 同語意；獨立一份避免 context 依賴型別轉換 */
function toInternalPathSafe(payload: unknown): string | null {
  const url = (payload as { url?: unknown } | null | undefined)?.url;
  if (typeof url !== "string" || url === "") return null;
  const path = url.replace(/^https?:\/\/[^/]+/, "");
  return path.startsWith("/") ? path : null;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be inside <NotificationProvider>");
  return ctx;
}
